import { cacheKey } from '@/constants/cache-key';
import {
	type IApiResponse,
	type IElectoralDivisionMetaDataFormHook,
	type IGetByPollingUnitCode,
	type IPollingUnitByIDState,
	type IPollingUnitInWardsStates,
	type IPollingUnitStates,
} from '@/helpers/types';
import { getCoreQuery, type IGetQuery } from '@/store/actions/api-utils';
import { type IPollingUnitCreateUpdate } from '@/store/actions/core/poolingunit';

import { coreApiSlice } from '../apiSlice';

const pollingUnitApiSlice = coreApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		createPollingUnit: builder.mutation<IApiResponse, IPollingUnitCreateUpdate>({
			query: (body) => ({
				url: '/PoolingUnit',
				method: 'POST',
				body,
			}),
		}),
		getPollingUnit: builder.query<IPollingUnitStates, IGetQuery>({
			query: ({ paged, query }) => ({
				url: `/PoolingUnit${getCoreQuery(paged, ...(query || []))}`,
			}),
		}),
		getPollingUnitById: builder.query<IApiResponse<IPollingUnitByIDState>, { id: string }>({
			query: ({ id }) => ({
				url: `/PoolingUnit/${id}`,
			}),
		}),
		getPollingUnitByName: builder.mutation<IApiResponse, { name: string }>({
			query: (body) => ({
				url: '/PoolingUnit/name',
				method: 'POST',
				body,
			}),
		}),
		getPollingUnitByCode: builder.mutation<IGetByPollingUnitCode, { poolingUnitCode: string }>({
			query: (body) => ({
				url: '/PoolingUnit/code',
				method: 'POST',
				body,
			}),
		}),
		updatePollingUnit: builder.mutation<IApiResponse, IPollingUnitCreateUpdate>({
			query: (body) => ({
				method: 'PUT',
				url: '/PoolingUnit/update',
				body,
			}),
		}),
		getPollingUnitPresidingOfficer: builder.mutation<IApiResponse, { presidingOfficerId: number; poolingUnitID: number }>({
			query: ({ presidingOfficerId, poolingUnitID }) => ({
				url: `/PoolingUnit/PresidingOfficer?presidingOfficerId=${presidingOfficerId}&poolingUnitID=${poolingUnitID}`,
				method: 'POST',
			}),
		}),
		getPollingUnitInWard: builder.mutation<
			IPollingUnitInWardsStates,
			{ wardIds: number[]; PageNumber?: number; PageSize?: number; paged?: boolean; search?: string }
		>({
			query: ({ wardIds, PageNumber, PageSize, paged = false, search }) => ({
				url: `/PoolingUnit/getPollingUnitsInWard${getCoreQuery(paged)}`,
				method: 'POST',
				body: {
					wardIds: wardIds,
					PageNumber: PageNumber || 1,
					PageSize: PageSize || 20,
					...(search ? { search } : {}),
				},
			}),
		}),
		deletePollingUnit: builder.mutation<IApiResponse, { id: string }>({
			query: ({ id }) => ({
				url: `/PoolingUnit/delete/${id}`,
				method: 'DELETE',
			}),
		}),
		updatePollingUnitMetaData: builder.mutation<IApiResponse, { id: number; data: IElectoralDivisionMetaDataFormHook }>({
			query: ({ id, data }) => ({
				url: `/PollingUnit/updatemetadata/${id}`,
				method: 'PUT',
				body: data,
			}),
		}),
	}),
});

export const {
	useCreatePollingUnitMutation,
	useDeletePollingUnitMutation,
	useGetPollingUnitByCodeMutation,
	useGetPollingUnitByIdQuery,
	useLazyGetPollingUnitByIdQuery,
	useGetPollingUnitByNameMutation,
	useGetPollingUnitInWardMutation,
	useGetPollingUnitPresidingOfficerMutation,
	useGetPollingUnitQuery,
	useLazyGetPollingUnitQuery,
	useUpdatePollingUnitMetaDataMutation,
	useUpdatePollingUnitMutation,
} = pollingUnitApiSlice;

export const selectGetPUInWard = pollingUnitApiSlice.endpoints.getPollingUnitInWard.select({
	fixedCacheKey: cacheKey.GETPUINWARD,
	requestId: 'pu-in-ward',
});

export const selectGetPU = pollingUnitApiSlice.endpoints.getPollingUnit.select({});
